import {
    Iteration,
    ProjectElementNode,
} from '@deltek/specification-client/core/Models/productListingDetail'
import {
    OverviewMetadata,
    ProductFamilyStudioManagement,
} from 'models/StudioManagement/studioManagement'
import { CategoryElementListStateInfo } from 'pages/ProjectsPage/WorkspacePage'
import { ElementItemIdentifier } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementTreeView'
import React from 'react'
import { ProjectElementNodeWithChild } from '..'

export interface SelectedIds {
    id: string
    publishStatus: boolean
    type: string
    nodeId: string
    rowIndex: number
    projectElementNodeId: string
    familyNumber: string
    familyName: string
    productFamilyId?: string
}

export interface SidebarSelection extends ProjectElementNodeWithChild {
    sectionTitle: string
}

export interface SearchProps {
    assembly: string | boolean | null
    divisions: string | boolean | null
    'sustainability topic'?: string | boolean | null
}

export interface StudioContextValue {
    assemblyStates: Record<string, CategoryElementListStateInfo>
    setAssemblyStates: React.Dispatch<
        React.SetStateAction<Record<string, CategoryElementListStateInfo>>
    >
    divisionStates: Record<string, CategoryElementListStateInfo>
    setDivisionStates: React.Dispatch<
        React.SetStateAction<Record<string, CategoryElementListStateInfo>>
    >
    topicStates: Record<string, CategoryElementListStateInfo>
    setTopicStates: React.Dispatch<
        React.SetStateAction<Record<string, CategoryElementListStateInfo>>
    >
    libraryState: ProjectElementNodeWithChild[]
    setLibraryState: React.Dispatch<ProjectElementNodeWithChild[]>
    createWorkItemLoading: boolean
    setCreateWorkItemLoading: React.Dispatch<React.SetStateAction<boolean>>
    selectedElementIdentifier: ElementItemIdentifier | undefined
    setSelectedElementIdentifier: React.Dispatch<
        React.SetStateAction<ElementItemIdentifier | undefined>
    >
    selectedPT: ProjectElementNodeWithChild | undefined | null
    setSelectedPT: React.Dispatch<
        React.SetStateAction<ProjectElementNodeWithChild | undefined | null>
    >
    selectedSection:
        | ProjectElementNodeWithChild
        | ProductFamilyStudioManagement
        | undefined
        | null
    setSelectedSection: React.Dispatch<
        React.SetStateAction<
            | ProjectElementNodeWithChild
            | ProductFamilyStudioManagement
            | undefined
            | null
        >
    >
    sidebarSelection?: SidebarSelection
    setSidebarSelection: React.Dispatch<
        React.SetStateAction<SidebarSelection | undefined>
    >
    selectedSectionDetails: OverviewMetadata | undefined | null
    setSelectedSectionDetails: React.Dispatch<
        React.SetStateAction<OverviewMetadata | undefined | null>
    >
    currentOMChildNodes: ProjectElementNode[] | undefined
    setCurrentOMChildNodes: React.Dispatch<
        React.SetStateAction<ProjectElementNode[] | undefined>
    >
    treeIndexerRefStudio: React.MutableRefObject<
        Map<string, Map<string, number>>
    >
    selectedIds: SelectedIds[]
    globalChekboxSelect?: boolean
    rowCheckBoxSelect?: boolean
    setSelectedIds?: (productFamilyId: SelectedIds, checked: boolean) => void
    setAllIds?: (ids: SelectedIds[]) => void
    setGlobalChekboxSelect?: (value: boolean) => void
    setRowCheckBoxSelect?: (value: boolean) => void
    sidebarClosed?: boolean
    setSidebarClosed?: (value: boolean) => void
    searchValue: SearchProps
    setSearchValue: React.Dispatch<React.SetStateAction<SearchProps>>
    expandSearch: SearchProps
    setExpandSearch: React.Dispatch<React.SetStateAction<SearchProps>>
    isSearched: SearchProps
    setIsSearched: React.Dispatch<React.SetStateAction<SearchProps>>
    selectedElementFormat?: string
    handleSelectedElementFormat?: (value: string) => void
    executedIntanceId?: string | null
    setExecutedInstanceId?: (val: string | null) => void
    hasDuplicateImportedSection?: boolean
    setHasDuplicateImportedSection?: (value: boolean) => void
    updateVersionIteration: (value: Iteration[]) => void
}

const defaultState = {
    selectedIds: [],
    globalChekboxSelect: false,
    rowCheckBoxSelect: false,
}

export const StudioContext = React.createContext<Partial<StudioContextValue>>(
    defaultState
)

export interface StudioProviderProps {
    assemblyStates: Record<string, CategoryElementListStateInfo>
    setAssemblyStates: React.Dispatch<
        React.SetStateAction<Record<string, CategoryElementListStateInfo>>
    >
    divisionStates: Record<string, CategoryElementListStateInfo>
    setDivisionStates: React.Dispatch<
        React.SetStateAction<Record<string, CategoryElementListStateInfo>>
    >
    libraryState: ProjectElementNodeWithChild[]
    setLibraryState: React.Dispatch<ProjectElementNodeWithChild[]>
    topicStates: Record<string, CategoryElementListStateInfo>
    setTopicStates: React.Dispatch<
        React.SetStateAction<Record<string, CategoryElementListStateInfo>>
    >
    createWorkItemLoading: boolean
    setCreateWorkItemLoading: React.Dispatch<React.SetStateAction<boolean>>
    selectedElementIdentifier: ElementItemIdentifier | undefined
    setSelectedElementIdentifier: React.Dispatch<
        React.SetStateAction<ElementItemIdentifier | undefined>
    >
    selectedSectionDetails: OverviewMetadata | undefined | null
    setSelectedSectionDetails: React.Dispatch<
        React.SetStateAction<OverviewMetadata | undefined | null>
    >
    currentOMChildNodes: ProjectElementNode[] | undefined
    setCurrentOMChildNodes: React.Dispatch<
        React.SetStateAction<ProjectElementNode[] | undefined>
    >
    selectedPT: ProjectElementNodeWithChild
    setSelectedPT: React.Dispatch<
        React.SetStateAction<ProjectElementNodeWithChild | undefined | null>
    >
    selectedSection:
        | ProjectElementNodeWithChild
        | ProductFamilyStudioManagement
        | undefined
        | null
    setSelectedSection: React.Dispatch<
        React.SetStateAction<
            | ProjectElementNodeWithChild
            | ProductFamilyStudioManagement
            | undefined
            | null
        >
    >
    sidebarSelection?: SidebarSelection
    setSidebarSelection: React.Dispatch<
        React.SetStateAction<SidebarSelection | undefined>
    >
    treeIndexerRefStudio: React.MutableRefObject<
        Map<string, Map<string, number>>
    >
    selectedIds: SelectedIds[]
    setSelectedIds?: (productFamilyId: SelectedIds, checked: boolean) => void
    setAllIds?: (ids: SelectedIds[]) => void
    setGlobalChekboxSelect?: (value: boolean) => void
    setRowCheckBoxSelect?: (value: boolean) => void
    globalChekboxSelect?: boolean
    rowCheckBoxSelect?: boolean
    sidebarClosed?: boolean
    setSidebarClosed?: (value: boolean) => void
    selectedElementFormat?: string
    handleSelectedElementFormat?: (value: string) => void
    executedIntanceId?: string | null
    setExecutedInstanceId?: (val: string | null) => void
    hasDuplicateImportedSection?: boolean
    setHasDuplicateImportedSection?: (value: boolean) => void
}

export const StudioContextProvider: React.FC<StudioProviderProps> = ({
    children,
}) => {
    const [selectedIds, setSelectIds] = React.useState<SelectedIds[]>([])
    const [globalChekboxSelect, setglobalChekboxSelect] = React.useState(false)
    const [rowCheckBoxSelect, setrowCheckBoxSelect] = React.useState(false)
    const [executedIntanceId, setInstanceId] = React.useState<string | null>(
        null
    )

    const treeIndexerRefStudio = React.useRef(
        new Map<string, Map<string, number>>()
    )
    const [selectedElementFormat, setSelectedElementFormat] = React.useState<
        string
    >('')
    const [
        selectedElementIdentifier,
        setSelectedElementIdentifier,
    ] = React.useState<ElementItemIdentifier | undefined>()

    const [createWorkItemLoading, setCreateWorkItemLoading] = React.useState<
        boolean
    >(false)

    const [selectedSection, setSelectedSection] = React.useState<
        | ProjectElementNodeWithChild
        | ProductFamilyStudioManagement
        | undefined
        | null
    >()

    const [sidebarSelection, setSidebarSelection] = React.useState<
        SidebarSelection | undefined
    >()

    const [selectedSectionDetails, setSelectedSectionDetails] = React.useState<
        OverviewMetadata | undefined | null
    >()

    const [currentOMChildNodes, setCurrentOMChildNodes] = React.useState<
        ProjectElementNode[] | undefined
    >()

    const [selectedPT, setSelectedPT] = React.useState<
        ProjectElementNodeWithChild | undefined | null
    >()

    const [assemblyStates, setAssemblyStates] = React.useState<
        Record<string, CategoryElementListStateInfo>
    >({})
    const [divisionStates, setDivisionStates] = React.useState<
        Record<string, CategoryElementListStateInfo>
    >({})

    const [sidebarClosed, setSidebarClosed] = React.useState<boolean>(false)

    const [libraryState, setLibraryState] = React.useState<
        ProjectElementNodeWithChild[]
    >([])

    const [topicStates, setTopicStates] = React.useState<
        Record<string, CategoryElementListStateInfo>
    >({})

    const [searchValue, setSearchValue] = React.useState<SearchProps>({
        assembly: null,
        divisions: null,
        'sustainability topic': null,
    })
    const [expandSearch, setExpandSearch] = React.useState<SearchProps>({
        assembly: null,
        divisions: null,
        'sustainability topic': null,
    })
    const [isSearched, setIsSearched] = React.useState<SearchProps>({
        assembly: false,
        divisions: false,
        'sustainability topic': false,
    })
    const [
        hasDuplicateImportedSection,
        setHasDuplicateImportedSection,
    ] = React.useState<boolean>(false)

    const setSelectedIds = React.useCallback(
        (ids: SelectedIds, checked: boolean) => {
            if (checked) {
                setSelectIds((prevIds) => [...prevIds, ids])
            } else {
                setSelectIds &&
                    setSelectIds(
                        selectedIds?.filter((item) => item.id !== ids?.id)
                    )
            }
        },
        [setSelectIds, selectedIds]
    )

    const setAllIds = React.useCallback(
        (ids: SelectedIds[]) => {
            setSelectIds([...ids])
        },
        [setSelectIds, selectedIds]
    )

    const setGlobalChekboxSelect = React.useCallback(
        (value: boolean) => {
            setglobalChekboxSelect(value)
            setrowCheckBoxSelect(false)
        },
        [
            setglobalChekboxSelect,
            setrowCheckBoxSelect,
            rowCheckBoxSelect,
            globalChekboxSelect,
        ]
    )

    const setRowCheckBoxSelect = React.useCallback(
        (value: boolean) => {
            if (value) {
                setglobalChekboxSelect(false)
            }
            setrowCheckBoxSelect(value)
        },
        [
            setglobalChekboxSelect,
            setrowCheckBoxSelect,
            rowCheckBoxSelect,
            globalChekboxSelect,
        ]
    )
    const handleSelectedElementFormat = (value: string) => {
        setSelectedElementFormat(value)
    }
    const setExecutedInstanceId = React.useCallback(
        (value: string | null) => {
            setInstanceId(value)
        },
        [setInstanceId]
    )

    const updateVersionIteration = React.useCallback(
        (updatedNodes) => {
            // creating a map of updatedNodes for quick look up
            const updatedNodesMap = new Map(
                updatedNodes.map((node: Iteration) => [node.id, node])
            )

            // mapping through the childNodes and replacing the node if the ids match
            const currentUpdatedNodes = currentOMChildNodes?.map((node) => {
                const updatedNode = updatedNodesMap.get(node?.iteration?.id)
                return updatedNode ? { ...node, iteration: updatedNode } : node
            })

            // updating existing library state for real time update
            const updatedLibraryState = libraryState?.map((node) => {
                if (selectedSection?.id === node.id) {
                    return { ...node, children: currentUpdatedNodes }
                }
                return node
            })

            setLibraryState(
                updatedLibraryState as ProjectElementNodeWithChild[]
            )
            setCurrentOMChildNodes(currentUpdatedNodes as ProjectElementNode[])
        },
        [currentOMChildNodes, libraryState, selectedSection]
    )

    return (
        <StudioContext.Provider
            value={{
                divisionStates,
                setDivisionStates,
                assemblyStates,
                setAssemblyStates,
                topicStates,
                setTopicStates,
                libraryState,
                setLibraryState,
                createWorkItemLoading,
                setCreateWorkItemLoading,
                selectedElementIdentifier,
                setSelectedElementIdentifier,
                treeIndexerRefStudio,
                selectedIds,
                setSelectedIds,
                setAllIds,
                setGlobalChekboxSelect,
                setRowCheckBoxSelect,
                globalChekboxSelect,
                rowCheckBoxSelect,
                selectedPT,
                selectedSection,
                setSelectedSection,
                sidebarSelection,
                setSidebarSelection,
                selectedSectionDetails,
                setSelectedSectionDetails,
                setCurrentOMChildNodes,
                currentOMChildNodes,
                setSelectedPT,
                sidebarClosed,
                setSidebarClosed,
                searchValue,
                setSearchValue,
                expandSearch,
                setExpandSearch,
                isSearched,
                setIsSearched,
                selectedElementFormat,
                handleSelectedElementFormat,
                setExecutedInstanceId,
                executedIntanceId,
                hasDuplicateImportedSection,
                setHasDuplicateImportedSection,
                updateVersionIteration,
            }}
        >
            {children}
        </StudioContext.Provider>
    )
}
